import { Identifier } from 'src/types/utilityTypes';
import api from 'src/utils/ajax';
import { decamelizeKeys } from 'src/utils/decamelize-keys';

import { fetchServices } from '../appointment/appointment';

import { Package, PackageListItem, PackageSearchResult } from './_types';
import { normalizePackage, normalizePackageSearchResult } from './normalizers';

interface FetchPackageOptions {
  id: Package['id'];
}

export const createPackage = async (
  data: Partial<Package>
): Promise<Package> => {
  const transformedData = decamelizeKeys(data, { deep: true });

  const response = await api.post('/api/v4/fbm/packages/', transformedData);

  return normalizePackage(response.data);
};

export const fetchPackage = async ({
  id,
}: FetchPackageOptions): Promise<Package> => {
  const response = await api.get(`/api/v4/fbm/packages/${id}`);
  return normalizePackage(response.data);
};

export interface FetchPackagesOptions {
  ids: Package['id'][];
}

export const packageListQueryKey = 'packageList';
export const fetchPackageOptions = async (
  trainerId?: Identifier
): Promise<PackageListItem[]> => {
  const response = await api.get('/api/v4/fbm/packages', {
    params: {
      fetch_all: true,
      pluck_columns: 'id,name',
      trainer_id: trainerId,
      'q[s]': 'name asc',
    },
  });

  return response.data;
};

export const fetchPackages = async ({
  ids,
}: FetchPackagesOptions): Promise<Package[]> => {
  const response = await api.get(`/api/v4/fbm/packages/`, {
    params: {
      with_ids: ids,
    },
  });

  let packages = (response.data.package || response.data).map(normalizePackage);

  const services = await fetchServices({ includePrivate: true });

  packages = packages.map((up) => {
    return {
      ...up,
      serviceCounts: up.serviceCounts.map((sc) => {
        return {
          ...sc,
          services: services.filter((s) => sc.serviceIds.includes(s.id)),
        };
      }),
    };
  });

  return packages;
};

export const fetchPackagesByParams = async (
  params = {}
): Promise<Package[]> => {
  const response = await api.get('/api/v4/fbm/packages', {
    params: { ...params },
  });
  return response.data.map(normalizePackage);
};

export const packageImage = (pack: Package) => {
  if (pack?.hasImage) {
    return pack.imageUrl;
  } else {
    return undefined;
  }
};

export const searchPackages = async ({
  search,
}: {
  search?: string;
  id?: string | number;
}): Promise<PackageSearchResult[]> => {
  const response = await api.get('/sm/search', {
    params: {
      types: ['packages'],
      limit: 100,
      no_limit: true,
      term: search,
      page: 1,
    },
  });
  return response.data.results['packages'].map((r) =>
    normalizePackageSearchResult(r)
  );
};
